import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AcceptInvitePayload,
  Account,
  AuthTokens,
  DecodedAccessToken,
  InviteInfo,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  MeResponse,
  RefreshRequest,
  RefreshResponse,
  Role,
} from '../models';
import { decodeJwtPayload } from '../utils/jwt.util';
import { TokenStorageService } from './token-storage.service';

/**
 * Owns the CMS staff session: login/refresh/logout and token persistence. The HTTP auth
 * interceptor calls `refresh()` directly on a 401.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Clock-drift allowance when reading `exp`; a token inside this window is already spent. */
  private static readonly EXPIRY_SKEW_MS = 5_000;

  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly baseUrl = `${environment.apiUrl}/auth`;

  private readonly currentUserSubject = new BehaviorSubject<Account | null>(
    this.tokenStorage.getUser(),
  );
  readonly currentUser$: Observable<Account | null> = this.currentUserSubject.asObservable();

  /** Coalesces concurrent 401s into a single in-flight refresh call. */
  private refreshInFlight$: Observable<AuthTokens> | null = null;

  get currentUser(): Account | null {
    return this.currentUserSubject.value;
  }

  get accessToken(): string | null {
    return this.tokenStorage.getAccessToken();
  }

  get refreshToken(): string | null {
    return this.tokenStorage.getRefreshToken();
  }

  /**
   * True when the stored session is usable: a **CMS** token that is either still valid or still
   * refreshable.
   *
   * Presence alone was not enough. A long-expired token satisfied `authGuard`, so the shell
   * rendered and every screen whose reads the API allows anonymously looked healthy, while
   * `/users` — the one CMS list gated on an authenticated admin — failed on its own. That is what
   * "the Admins screen won't load" turned out to be: not a broken screen, a dead session that
   * nothing had noticed.
   *
   * The three rejections, in the order they are checked:
   *
   * 1. **Unparseable or absent** — nothing to reason about, and no request built from it can
   *    succeed. Re-login is the only exit.
   * 2. **Wrong realm** — an `app` token cannot drive the CMS however fresh it is, and refreshing
   *    returns a token for the same realm, so this is terminal rather than recoverable. Mirrors the
   *    server's realm check and `sessionRoles()`.
   * 3. **Expired with nothing to refresh from** — a spent session.
   *
   * An expired token *with* a refresh token stays authenticated on purpose: the interceptor repairs
   * it on the first 401, which is a better outcome than dumping an admin at /login mid-task.
   */
  isAuthenticated(): boolean {
    const decoded = this.decodeAccessToken();
    if (!decoded) {
      // No token, or one this build cannot parse. Either way there is nothing to act on.
      return false;
    }
    if (decoded.realm !== 'cms') {
      return false;
    }
    return !this.isAccessTokenExpired() || !!this.refreshToken;
  }

  /**
   * Whether the access token's `exp` has passed. An unparseable or `exp`-less token counts as
   * expired: failing closed is the only safe reading of a token we cannot date.
   *
   * The skew absorbs clock drift between the browser and the API, so a token that is about to
   * lapse mid-flight is refreshed before it is sent rather than after it is rejected.
   */
  isAccessTokenExpired(): boolean {
    const decoded = this.decodeAccessToken();
    if (!decoded || typeof decoded.exp !== 'number') {
      return true;
    }
    return decoded.exp * 1000 <= Date.now() + AuthService.EXPIRY_SKEW_MS;
  }

  /**
   * Whether a request should refresh **before** it is sent, rather than waiting for a 401.
   *
   * Waiting for the 401 was the bug behind "after a while the CMS only shows published courses".
   * Most catalogue reads run through `optionalAuth` on the server, which serves published rows to
   * anyone and drafts to staff — and it *swallows* an invalid or expired bearer token rather than
   * rejecting it, by design, so that a public caller with a stale token still gets the public view.
   * The CMS therefore never saw a 401 to react to: it kept sending its spent token, kept getting
   * 200s, and quietly rendered the anonymous catalogue. Nothing looked broken; the drafts were just
   * gone. Only `/users`-style routes, which do demand a session, ever produced the 401 that repaired
   * the token — so whether the session healed depended on which screen the author happened to open.
   *
   * The rule is narrow on purpose: refresh only when the token **parses** and its own `exp` says it
   * is spent. A token this build cannot read is left alone and sent as-is — we cannot date it, so
   * the server's answer is better evidence than our guess, and the 401 path still covers it. That is
   * the opposite reading from `isAccessTokenExpired`, which fails closed because it answers a
   * different question: whether the session can still be trusted, not whether spending a refresh now
   * is worthwhile.
   *
   * No refresh token means there is nothing to refresh *with*; the request goes out as it is and the
   * 401 path ends the session.
   */
  shouldRefreshBeforeRequest(): boolean {
    const decoded = this.decodeAccessToken();
    if (!decoded || typeof decoded.exp !== 'number') {
      return false;
    }
    if (decoded.exp * 1000 > Date.now() + AuthService.EXPIRY_SKEW_MS) {
      return false;
    }
    return !!this.refreshToken;
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, credentials)
      .pipe(tap((response) => this.setSession(response)));
  }

  /**
   * Reads an invite link's token, so the set-password page can name whose invite it is rather than
   * asking a stranger to type a password into an unlabelled form.
   *
   * Public — the invitee has no session yet, and acquiring one is the point. The token is the
   * authentication; a dead or unknown one is one 400 with one message, so probing tells an attacker
   * nothing about which guesses were once real.
   */
  inviteInfo(token: string): Observable<InviteInfo> {
    return this.http.get<InviteInfo>(`${this.baseUrl}/invite/${encodeURIComponent(token)}`);
  }

  /**
   * Sets the invitee's first password and opens their session in one call.
   *
   * `setSession` on the way through, exactly as `login` does — the server signs them in as part of
   * accepting, so landing them on the login screen to retype the password they just chose would be
   * asking twice for the same thing.
   */
  acceptInvite(payload: AcceptInvitePayload): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/invite/accept`, payload)
      .pipe(tap((response) => this.setSession(response)));
  }

  /** Rotates the refresh token — always persists the new one returned by the server. */
  refresh(): Observable<AuthTokens> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const body: RefreshRequest = { refreshToken };
    this.refreshInFlight$ = this.http.post<RefreshResponse>(`${this.baseUrl}/refresh`, body).pipe(
      tap((tokens) => this.tokenStorage.setTokens(tokens)),
      finalize(() => (this.refreshInFlight$ = null)),
      shareReplay(1),
    );
    return this.refreshInFlight$;
  }

  logout(): Observable<void> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    const request$ = refreshToken
      ? this.http.post<void>(`${this.baseUrl}/logout`, { refreshToken } satisfies LogoutRequest)
      : of(undefined);

    return request$.pipe(
      tap({ next: () => this.clearSession(), error: () => this.clearSession() }),
    ) as Observable<void>;
  }

  /**
   * `GET /auth/me` returns `{ account, realm, roles }` — the roles belong to the *session* rather
   * than to the account, so they are folded onto the cached account instead of being dropped.
   */
  me(): Observable<Account> {
    return this.http.get<MeResponse>(`${this.baseUrl}/me`).pipe(
      map((response) => ({
        ...response.account,
        roles: response.roles,
        role: response.roles[0] ?? null,
      })),
      tap((account) => {
        this.tokenStorage.setUser(account);
        this.currentUserSubject.next(account);
      }),
    );
  }

  /** Clears the local session without calling the server — used when a refresh fails. */
  clearSession(): void {
    this.tokenStorage.clear();
    this.currentUserSubject.next(null);
  }

  decodeAccessToken(): DecodedAccessToken | null {
    const token = this.accessToken;
    return token ? decodeJwtPayload<DecodedAccessToken>(token) : null;
  }

  /**
   * CMS role codes carried by the current session, or `[]` when there is no usable CMS token.
   * Every CMS account is an admin, so this is display-only — nothing branches on it.
   *
   * A non-`cms` session yields nothing, mirroring the server's realm check: an `app` token cannot
   * satisfy a CMS role requirement even if it claims a CMS role code.
   */
  sessionRoles(): Role[] {
    const decoded = this.decodeAccessToken();
    if (!decoded || decoded.realm !== 'cms') {
      return [];
    }
    // Falls back to the singular claim so a token minted before `roles` existed still works.
    const roles = decoded.roles ?? (decoded.role ? [decoded.role] : []);
    return roles.filter((code): code is Role => code !== 'learner');
  }

  private setSession(response: LoginResponse): void {
    this.tokenStorage.setTokens(response);
    // `user` is the API's deprecated alias for `account`; prefer `account` and tolerate either.
    const account = response.account ?? response.user;
    if (account) {
      this.tokenStorage.setUser(account);
      this.currentUserSubject.next(account);
    }
  }
}
